import React, { useState, useEffect } from "react";
import axios from "axios";
import { NavLink, useNavigate, Link } from "react-router-dom";
import "./Navbar.css";
import parse from "html-react-parser";
import { useAppContext } from "../../contexts/AppContext";
import NavCard from "../Card/NavCard";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [showServices, setShowServices] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [serviceslist, setServicesList] = useState([]);

  const {
    backendUrl,
    isAdmin,
    setAdminStatus,
    currentLanguage,
    changeLanguage,
    updateFullData,
    updateSelectedData,
    updateSelectedTitle,
    translate,
    getImageUrl,
  } = useAppContext();

  const companyRoute = "/company";
  const navigate = useNavigate();
  const serviceRoute = "/ServiceList";

  const languageList = [
    { label: "English", value: "eng", short: "En" },
    { label: "አማርኛ", value: "amh", short: "Am" },
    { label: "ትግርኛ", value: "tig", short: "Ti" },
  ];

  const currentLangLabel = languageList.find(l => l.value === currentLanguage)?.short || "En";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${backendUrl}${companyRoute}`);
        if (Array.isArray(response?.data) && response?.data?.length > 0) {
          setLogo(response?.data[0]?.images?.[0] || null);
        }

        const serviceResponse = await axios.get(`${backendUrl}${serviceRoute}`);
        if (Array.isArray(serviceResponse.data)) {
          setServicesList(serviceResponse.data);
        }
        setIsLoaded(true);
      } catch (error) {
        setIsLoaded(true);
      }
    };
    fetchData();
  }, [backendUrl]);

  const handleNavClick = (sectionId, effects) => {
    setActiveSection(sectionId);
    
    // 1. Always navigate to home with the hash
    navigate(`/#${sectionId}`);

    // 2. Immediate scroll if we are already on the home page
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    
    if (effects !== "no-effect") setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setAdminStatus(false);
    navigate("/login");
  };

  if (!isLoaded) return null;

  return (
    <header className="navbar-containers">
      <div className="nav-logo-container">
        <NavLink to="/" onClick={() => handleNavClick("home")}>
          {logo ? (
            <img className="nav-logo" src={getImageUrl(logo)} alt="logo" />
          ) : (
            <span className="fw-bold fs-3" style={{ color: "var(--primary-color)" }}>DR. TESFA</span>
          )}
        </NavLink>
      </div>

      <div className="right-container">
        <nav className={`nav-list-container ${mobileMenuOpen ? "open" : ""}`}>
          <div className="nav-items-container">
            <Link to="/" className="nav-items" onClick={() => handleNavClick("home")}>
              {parse(translate("Home"))}
            </Link>
          </div>

          <div className={`nav-items-container nav-items-containers ${showServices ? "open" : ""}`}>
            <span
              className={`nav-items ${activeSection === "services" ? "active" : ""}`}
              onClick={() => setShowServices(!showServices)}
            >
              {parse(translate("Services"))}{" "}
              <span className={`bi ${showServices ? "bi-chevron-up" : "bi-chevron-down"} ps-1`} style={{ fontSize: "12px" }}></span>
            </span>

            {serviceslist.length > 0 && (
              <div className="services-list shadow">
                {serviceslist.map((service, index) => (
                  <Link
                    key={index}
                    to={`/services/${service.id}`}
                    className="nav-sublist text-dark"
                    onClick={() => {
                      updateFullData(serviceslist);
                      updateSelectedData(service);
                      updateSelectedTitle("Services");
                      setShowServices(false);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <NavCard image={service?.images?.[0]} text={parse(translate(service?.title))} />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="nav-items-container">
            <span className="nav-items" onClick={() => handleNavClick("products")}>
              {parse(translate("Our Team"))}
            </span>
          </div>

          <div className="nav-items-container">
            <span className="nav-items" onClick={() => handleNavClick("portfolio")}>
              {parse(translate("Gallery"))}
            </span>
          </div>

          <div className="nav-items-container">
            <span className="nav-items" onClick={() => handleNavClick("blog")}>
              {parse(translate("Blog"))}
            </span>
          </div>

          <div className="nav-items-container">
            <span className="nav-items" onClick={() => handleNavClick("contact")}>
              {parse(translate("Contact Us"))}
            </span>
          </div>

          {isAdmin && (
            <div className="nav-items-container">
              <Link to="/admin" className="nav-items text-primary" onClick={() => setMobileMenuOpen(false)}>
                {parse(translate("Admin"))}
              </Link>
            </div>
          )}
        </nav>

        <div className="d-flex align-items-center gap-3">
          {/* Custom Language Selector */}
          <div
            className={`custom-lang-selector shadow-sm ${langOpen ? "lang-open" : ""}`}
            onClick={() => setLangOpen(!langOpen)}
            onBlur={() => setLangOpen(false)}
            tabIndex={0}
          >
            <i className="bi bi-translate lang-icon"></i>
            <span className="lang-current">{currentLangLabel}</span>
            <div className={`lang-dropdown shadow ${langOpen ? "lang-dropdown-visible" : ""}`}>
              {languageList.map((lang) => (
                <div
                  key={lang.value}
                  className="lang-opt pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage(lang.value);
                    setLangOpen(false);
                  }}
                >
                  {lang.label}
                </div>
              ))}
            </div>
          </div>

          {isAdmin && (
            <div className="d-none d-lg-block">
              <button className="btn btn-sm btn-outline-danger rounded-pill px-3" onClick={handleLogout}>
                {parse(translate("Logout"))}
              </button>
            </div>
          )}

          <div className="togle-button">
            <div
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`pointer d-flex align-items-center justify-content-center ${mobileMenuOpen ? "text-danger" : "text-primary"}`}
              style={{ width: "45px", height: "45px", background: "#f1f2f6", borderRadius: "12px" }}
            >
              <i className={`bi ${mobileMenuOpen ? "bi-x-lg" : "bi-list"} fs-2`}></i>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
